/**
 * Logger Utility
 *
 * Provides structured logging with sensitive data redaction in production.
 * Follows the established pattern from sw4p-console.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

const IS_DEV = import.meta.env.DEV;
const CURRENT_LEVEL: LogLevel = IS_DEV ? 'debug' : 'warn';

// Patterns to redact in production
const SENSITIVE_KEYS = ['privateKey', 'secret', 'signature', 'permit', 'token', 'password', 'apiKey'];
const ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const SOLANA_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const TX_HASH_PATTERN = /^0x[a-fA-F0-9]{64}$/;

function shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[CURRENT_LEVEL];
}

/**
 * Sanitize sensitive data for production logging
 */
function sanitize(data: unknown): unknown {
    // No sanitization in dev mode
    if (IS_DEV) return data;

    if (data === null || data === undefined) return data;

    if (typeof data === 'string') {
        // Truncate EVM addresses
        if (ADDRESS_PATTERN.test(data)) {
            return `${data.slice(0, 6)}...${data.slice(-4)}`;
        }
        // Truncate Solana addresses
        if (SOLANA_ADDRESS_PATTERN.test(data) && data.length > 32) {
            return `${data.slice(0, 4)}...${data.slice(-4)}`;
        }
        // Truncate transaction hashes
        if (TX_HASH_PATTERN.test(data)) {
            return `${data.slice(0, 10)}...${data.slice(-6)}`;
        }
        // Redact JWT tokens
        if (data.startsWith('eyJ')) {
            return '[JWT_REDACTED]';
        }
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(sanitize);
    }

    if (typeof data === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            // Fully redact sensitive keys
            if (SENSITIVE_KEYS.some(k => key.toLowerCase().includes(k.toLowerCase()))) {
                sanitized[key] = '[REDACTED]';
            } else {
                sanitized[key] = sanitize(value);
            }
        }
        return sanitized;
    }

    return data;
}

function formatArgs(args: unknown[]): unknown[] {
    return args.map(sanitize);
}

/**
 * Main logger object
 */
export const logger = {
    debug: (prefix: string, ...args: unknown[]) => {
        if (shouldLog('debug')) {
            console.debug(prefix, ...formatArgs(args));
        }
    },

    info: (prefix: string, ...args: unknown[]) => {
        if (shouldLog('info')) {
            console.info(prefix, ...formatArgs(args));
        }
    },

    warn: (prefix: string, ...args: unknown[]) => {
        if (shouldLog('warn')) {
            console.warn(prefix, ...formatArgs(args));
        }
    },

    error: (prefix: string, ...args: unknown[]) => {
        if (shouldLog('error')) {
            console.error(prefix, ...formatArgs(args));
        }
    },

    /**
     * Log transaction events with structured data
     */
    tx: (event: string, data: Record<string, unknown>) => {
        if (shouldLog('info')) {
            console.info(`[TX:${event}]`, sanitize(data));
        }
    },
};

export default logger;
